import React, { useState } from 'react';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import { useAuth } from '../../contexts/AuthContext';
import FormComentario from './FormComentario';
import ConfirmDialog from '../common/ConfirmDialog';
import comunicacaoService from '../../services/comunicacaoService';
import { format } from 'date-fns';

function markdownParaHtml(md: string): string {
  return md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
}

function renderTextoFormatado(text: string): React.ReactNode {
  return <span dangerouslySetInnerHTML={{ __html: markdownParaHtml(text) }} />;
}

const DetalhesTopico: React.FC = () => {
  const { topicoSelecionado, comentarios, voltarParaCanal, removerComentario, editarComentario } = useComunicacao();
  const { usuario } = useAuth();
  const [comentarioEditando, setComentarioEditando] = useState<string | null>(null);
  const [mensagemEditando, setMensagemEditando] = useState('');
  const [modalConfirmacaoAberto, setModalConfirmacaoAberto] = useState(false);
  const [comentarioParaRemover, setComentarioParaRemover] = useState<string | null>(null);

  if (!topicoSelecionado) {
    return null;
  }

  const isAdmin = usuario?.tipo === 'admin';

  const handleRemoverComentario = (comentarioId: string) => {
    setComentarioParaRemover(comentarioId);
    setModalConfirmacaoAberto(true);
  };

  const confirmarRemocao = async () => {
    if (comentarioParaRemover) {
      try {
        await removerComentario(comentarioParaRemover);
        setModalConfirmacaoAberto(false);
        setComentarioParaRemover(null);
      } catch (error) {
        console.error('Erro ao remover comentário:', error);
      }
    }
  };

  const handleEditarComentario = (comentarioId: string, mensagemAtual: string) => {
    setComentarioEditando(comentarioId);
    setMensagemEditando(mensagemAtual);
  };

  const handleSalvarEdicao = async (comentarioId: string) => {
    if (!mensagemEditando.trim()) return;
    
    try {
      await editarComentario(comentarioId, { mensagem: mensagemEditando.trim() });
      setComentarioEditando(null);
      setMensagemEditando('');
    } catch (error) {
      console.error('Erro ao editar comentário:', error);
    }
  };

  const handleCancelarEdicao = () => {
    setComentarioEditando(null);
    setMensagemEditando('');
  };

  const handleDownloadAnexo = async (anexoId: string, nomeOriginal: string) => {
    try {
      const url = await comunicacaoService.downloadAnexo(anexoId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao fazer download do anexo:', error);
    }
  };

  const formatarTamanho = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getIconeAnexo = (tipo: string) => {
    if (tipo.startsWith('image/')) {
      return (
        <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    if (tipo === 'application/pdf') {
      return (
        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    return (
      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-y-auto">
      <div className="border-b border-gray-200 p-4">
        <button
          onClick={voltarParaCanal}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Voltar para #{topicoSelecionado.canal_nome}
        </button>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">{topicoSelecionado.titulo}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <img
                  src={topicoSelecionado.autor_avatar || '/profilepicture.png'}
                  alt={topicoSelecionado.autor_nome}
                  className="w-6 h-6 rounded-full"
                />
                <span className="font-medium">{topicoSelecionado.autor_nome}</span>
                <span>•</span>
                <span>
                  {format(new Date(topicoSelecionado.data_criacao), 'dd/MM/yyyy HH:mm')}
                </span>
              </div>
              <div className="text-gray-700 leading-relaxed">{renderTextoFormatado(topicoSelecionado.mensagem)}</div>
              
              {topicoSelecionado.anexos && topicoSelecionado.anexos.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Anexos
                  </h3>
                  <div className="space-y-2">
                    {topicoSelecionado.anexos.map((anexo) => (
                      <div
                        key={anexo.id}
                        className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {getIconeAnexo(anexo.tipo)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {anexo.nome_original}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatarTamanho(anexo.tamanho)} • {anexo.usuario_nome}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadAnexo(anexo.id, anexo.nome_original)}
                          className="ml-2 p-2 text-bravo-primary hover:bg-blue-50 rounded transition-colors"
                          title="Baixar anexo"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {comentarios.length} {comentarios.length === 1 ? 'Comentário' : 'Comentários'}
          </h2>

          <div className="space-y-4">
            {comentarios.map((comentario) => (
              <div key={comentario.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <img
                      src={comentario.autor_avatar || '/profilepicture.png'}
                      alt={comentario.autor_nome}
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <div className="font-medium text-gray-800">{comentario.autor_nome}</div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(comentario.data_criacao), 'dd/MM/yyyy HH:mm')}
                      </div>
                    </div>
                  </div>

                  {(comentario.autor_id === usuario?.id || isAdmin) && (
                    <div className="flex gap-2">
                      {comentarioEditando !== comentario.id && (
                        <button
                          onClick={() => handleEditarComentario(comentario.id, comentario.mensagem)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Editar comentário"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoverComentario(comentario.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remover comentário"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {comentarioEditando === comentario.id ? (
                  <div className="mt-2">
                    <textarea
                      value={mensagemEditando}
                      onChange={(e) => setMensagemEditando(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary resize-none"
                      rows={3}
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleSalvarEdicao(comentario.id)}
                        className="px-3 py-1 bg-bravo-primary text-white rounded-md hover:bg-blue-600 text-sm"
                      >
                        Salvar
                      </button>
                      <button
                        onClick={handleCancelarEdicao}
                        className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-700 leading-relaxed">{renderTextoFormatado(comentario.mensagem)}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4">
          <FormComentario topicoId={topicoSelecionado.id} />
        </div>
      </div>

      <ConfirmDialog
        open={modalConfirmacaoAberto}
        title="Remover Comentário"
        message="Tem certeza que deseja remover este comentário? Esta ação não pode ser desfeita."
        confirmText="Remover"
        onConfirm={confirmarRemocao}
        onCancel={() => {
          setModalConfirmacaoAberto(false);
          setComentarioParaRemover(null);
        }}
      />
    </div>
  );
};

export default DetalhesTopico;
